import React, { useState, useEffect } from 'react';
import { Trash2, Loader2, AlertTriangle, Package, RefreshCw, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { admin } from '@/lib/api';
import { toast } from 'sonner';

const BulkDeleteSellerProducts = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmStep, setConfirmStep] = useState(0); // 0: none, 1: first alert, 2: final confirm
  const [deleting, setDeleting] = useState(false);
  const isTransitioning = React.useRef(false);

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/sellers-with-products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSellers(data.sellers);
      }
    } catch (error) {
      console.error('Failed to fetch sellers:', error);
      toast.error('Satıcı listesi alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (seller) => {
    if (seller.product_count === 0) {
      toast.info('Bu satıcının hiç ürünü yok.');
      return;
    }
    setDeleteTarget(seller);
    setConfirmStep(1);
  };

  const handleFirstConfirm = () => {
    isTransitioning.current = true;
    setConfirmStep(2);
    setTimeout(() => { isTransitioning.current = false; }, 100);
  };

  const handleFinalDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/seller/${deleteTarget.id}/products`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(`${data.deleted_count} ürün silindi: ${deleteTarget.email}`);
        fetchSellers();
      } else {
        toast.error(data.detail || 'Silme işlemi başarısız');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Silme işlemi sırasında hata oluştu');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
      setConfirmStep(0);
    }
  };

  const handleCancel = () => {
    setDeleteTarget(null);
    setConfirmStep(0);
  };

  // Only cancel on explicit dismiss, not on step transition
  const handleDialogOpenChange = (open) => {
    if (!open && !isTransitioning.current && !deleting) {
      handleCancel();
    }
  };

  const filteredSellers = sellers.filter(s => 
    s.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.store_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bulk-delete-seller-products">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="w-5 h-5" />
                Satıcı Ürünlerini Toplu Sil
              </CardTitle>
              <CardDescription className="mt-1">
                Bir satıcının tüm ürünlerini silmek için kullanın. Bu işlem geri alınamaz!
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchSellers} data-testid="refresh-sellers-btn">
              <RefreshCw className="w-4 h-4 mr-1" />
              Yenile
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Satıcı ara (email, isim)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="seller-search-input"
            />
          </div>

          {/* Sellers Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Satıcı</TableHead>
                  <TableHead>Mağaza</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead className="text-center">Ürün Sayısı</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSellers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      {searchQuery ? 'Arama sonucu bulunamadı' : 'Satıcı bulunamadı'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSellers.map((s) => (
                    <TableRow key={s.id} data-testid={`seller-row-${s.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{s.name || '-'}</p>
                          <p className="text-xs text-muted-foreground">{s.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{s.store_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={s.seller_status === 'approved' ? 'default' : 'secondary'} className="text-xs">
                          {s.seller_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Package className="w-3.5 h-3.5 text-gray-400" />
                          <span className={`font-bold ${s.product_count > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                            {s.product_count.toLocaleString()}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="destructive" 
                          size="sm"
                          disabled={s.product_count === 0}
                          onClick={() => handleDeleteClick(s)}
                          data-testid={`delete-btn-${s.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Tümünü Sil
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* First Confirmation Dialog */}
      <AlertDialog open={confirmStep === 1} onOpenChange={handleDialogOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Ürünleri Silmek İstediğinize Emin Misiniz?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  <strong>{deleteTarget?.email}</strong> satıcısının{' '}
                  <strong className="text-red-600">{deleteTarget?.product_count?.toLocaleString()}</strong>{' '}
                  ürünü silinecek.
                </p>
                <p className="text-amber-600 font-medium">
                  Bu işlem geri alınamaz. Devam etmek istediğinize emin misiniz?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel} data-testid="cancel-delete-btn">İptal</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleFirstConfirm}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="first-confirm-btn"
            >
              Evet, Devam Et
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Final Confirmation Dialog */}
      <AlertDialog open={confirmStep === 2} onOpenChange={handleDialogOpenChange}>
        <AlertDialogContent className="border-red-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              SON ONAY - GERİ ALINAMAZ!
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 font-medium">Silinecek:</p>
                  <ul className="mt-2 space-y-1 text-red-700 text-sm">
                    <li>Satıcı: <strong>{deleteTarget?.email}</strong></li>
                    <li>Ürün Sayısı: <strong>{deleteTarget?.product_count?.toLocaleString()}</strong></li>
                  </ul>
                </div>
                <p className="text-red-600 font-bold text-center">
                  Bu işlemi onaylıyor musunuz? Tüm ürünler kalıcı olarak silinecek!
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel} data-testid="cancel-final-btn">Vazgeç</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleFinalDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
              data-testid="final-delete-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Siliniyor...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  KALICI OLARAK SİL
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BulkDeleteSellerProducts;
